/**
 * Zentrale Definition aller Social Media Format-Spezifikationen
 * Diese Datei muss bei API-Änderungen aktualisiert werden!
 *
 * Letztes Update: Januar 2025
 */

export type Platform = 'instagram' | 'facebook';
export type MediaType = 'image' | 'video';
export type PostType = 'feed' | 'story' | 'reel' | 'carousel';

export interface FormatSpec {
  id: string;
  name: string;
  nameDe: string;
  platform: Platform;
  postType: PostType;
  mediaType: MediaType | 'both';
  aspectRatio: string;
  aspectRatioNumeric: number; // width / height
  dimensions: {
    width: number;
    height: number;
  };
  alternativeDimensions?: {
    width: number;
    height: number;
    label: string;
  }[];
  maxDuration?: number; // Sekunden
  minDuration?: number; // Sekunden
  recommendedDuration?: string;
  maxFileSize: string;
  maxFileSizeBytes: number;
  fileTypes: string[];
  tips: string[];
  apiLimitations?: string[];
  lastUpdated: string;
}

export interface FormatCategory {
  id: string;
  name: string;
  nameDe: string;
  aspectRatio: string;
  icon: string;
  description: string;
}

// Format-Kategorien für Gruppierung in der Medienbibliothek
export const FORMAT_CATEGORIES: FormatCategory[] = [
  {
    id: 'square',
    name: 'Square',
    nameDe: 'Quadrat',
    aspectRatio: '1:1',
    icon: '⬜',
    description: 'Ideal für Feed-Posts und Carousels',
  },
  {
    id: 'portrait',
    name: 'Portrait',
    nameDe: 'Hochformat',
    aspectRatio: '4:5',
    icon: '📱',
    description: 'Optimale Größe für Feed-Posts',
  },
  {
    id: 'story',
    name: 'Story/Reel',
    nameDe: 'Story/Reel',
    aspectRatio: '9:16',
    icon: '📲',
    description: 'Vollbild-Format für Stories und Reels',
  },
  {
    id: 'landscape',
    name: 'Landscape',
    nameDe: 'Querformat',
    aspectRatio: '16:9',
    icon: '🖥️',
    description: 'Für Link-Posts und Facebook-Videos',
  },
  {
    id: 'free',
    name: 'Free',
    nameDe: 'Frei',
    aspectRatio: 'frei',
    icon: '✂️',
    description: 'Freies Zuschneiden ohne festes Seitenverhältnis',
  },
];

// Alle Format-Spezifikationen
export const FORMAT_SPECS: FormatSpec[] = [
  // ============ INSTAGRAM ============
  {
    id: 'instagram_feed_image_square',
    name: 'Instagram Feed Post (Square)',
    nameDe: 'Instagram Feed-Beitrag (Quadrat)',
    platform: 'instagram',
    postType: 'feed',
    mediaType: 'image',
    aspectRatio: '1:1',
    aspectRatioNumeric: 1,
    dimensions: { width: 1080, height: 1080 },
    maxFileSize: '30 MB',
    maxFileSizeBytes: 30 * 1024 * 1024,
    fileTypes: ['JPEG', 'PNG'],
    tips: [
      'Quadratisches Format funktioniert auf allen Geräten gut',
      'Mindestauflösung: 320px, empfohlen: 1080px',
    ],
    lastUpdated: '2025-01-02',
  },
  {
    id: 'instagram_feed_image_portrait',
    name: 'Instagram Feed Post (Portrait)',
    nameDe: 'Instagram Feed-Beitrag (Hochformat)',
    platform: 'instagram',
    postType: 'feed',
    mediaType: 'image',
    aspectRatio: '4:5',
    aspectRatioNumeric: 0.8,
    dimensions: { width: 1080, height: 1350 },
    alternativeDimensions: [
      { width: 1080, height: 1440, label: 'Neues Grid-Format (3:4)' },
    ],
    maxFileSize: '30 MB',
    maxFileSizeBytes: 30 * 1024 * 1024,
    fileTypes: ['JPEG', 'PNG'],
    tips: [
      'Nimmt mehr Platz im Feed ein → höhere Sichtbarkeit',
      '4:5 ist das optimale Format für maximale Bildschirmnutzung',
      'Seit 2025: 3:4 (1080×1440) für neues Grid-Layout möglich',
    ],
    lastUpdated: '2025-01-02',
  },
  {
    id: 'instagram_feed_video',
    name: 'Instagram Feed Video',
    nameDe: 'Instagram Feed-Video',
    platform: 'instagram',
    postType: 'feed',
    mediaType: 'video',
    aspectRatio: '4:5',
    aspectRatioNumeric: 0.8,
    dimensions: { width: 1080, height: 1350 },
    alternativeDimensions: [
      { width: 1080, height: 1080, label: 'Quadrat (1:1)' },
    ],
    maxDuration: 3600, // 60 Minuten
    recommendedDuration: '30-60 Sekunden',
    maxFileSize: '4 GB',
    maxFileSizeBytes: 4 * 1024 * 1024 * 1024,
    fileTypes: ['MP4', 'MOV'],
    tips: [
      'Videos unter 15 Min werden automatisch als Reels behandelt',
      'Kürzere Videos (30-60 Sek) performen besser',
      'H.264 Codec, AAC Audio empfohlen',
    ],
    apiLimitations: [
      'Videos werden als Reels veröffentlicht',
    ],
    lastUpdated: '2025-01-02',
  },
  {
    id: 'instagram_reel',
    name: 'Instagram Reel',
    nameDe: 'Instagram Reel',
    platform: 'instagram',
    postType: 'reel',
    mediaType: 'video',
    aspectRatio: '9:16',
    aspectRatioNumeric: 0.5625,
    dimensions: { width: 1080, height: 1920 },
    minDuration: 3,
    maxDuration: 180, // 3 Minuten (neu 2025!)
    recommendedDuration: '15-30 Sekunden',
    maxFileSize: '4 GB',
    maxFileSizeBytes: 4 * 1024 * 1024 * 1024,
    fileTypes: ['MP4', 'MOV'],
    tips: [
      '15-30 Sekunden haben die beste Completion Rate',
      'Wichtige Elemente in der Mitte platzieren (Safe Zone)',
      'Cover-Bild kann gewählt werden (API-Support)',
      'Audio-Name kann angepasst werden',
    ],
    apiLimitations: [
      'Musik muss im Video enthalten sein (kein API-Upload)',
      'Max 90 Sek via API (manche Accounts 60 Sek)',
      'Sticker/Text nicht via API möglich',
    ],
    lastUpdated: '2025-01-02',
  },
  {
    id: 'instagram_story',
    name: 'Instagram Story',
    nameDe: 'Instagram Story',
    platform: 'instagram',
    postType: 'story',
    mediaType: 'both',
    aspectRatio: '9:16',
    aspectRatioNumeric: 0.5625,
    dimensions: { width: 1080, height: 1920 },
    maxDuration: 60,
    recommendedDuration: '15 Sekunden',
    maxFileSize: '4 GB',
    maxFileSizeBytes: 4 * 1024 * 1024 * 1024,
    fileTypes: ['JPEG', 'PNG', 'MP4', 'MOV'],
    tips: [
      'Stories verschwinden nach 24 Stunden',
      'Videos länger als 15 Sek werden in Segmente geteilt',
      'Vollbild-Format nutzen für beste Wirkung',
    ],
    apiLimitations: [
      'Keine Sticker via API (Polls, Fragen, Links)',
      'Keine interaktiven Elemente via API',
      'Nur fertige Bild-/Video-Dateien möglich',
    ],
    lastUpdated: '2025-01-02',
  },
  {
    id: 'instagram_carousel',
    name: 'Instagram Carousel',
    nameDe: 'Instagram Karussell',
    platform: 'instagram',
    postType: 'carousel',
    mediaType: 'both',
    aspectRatio: '4:5',
    aspectRatioNumeric: 0.8,
    dimensions: { width: 1080, height: 1350 },
    alternativeDimensions: [
      { width: 1080, height: 1080, label: 'Quadrat (1:1)' },
    ],
    maxDuration: 60, // pro Slide
    maxFileSize: '4 GB',
    maxFileSizeBytes: 4 * 1024 * 1024 * 1024,
    fileTypes: ['JPEG', 'PNG', 'MP4', 'MOV'],
    tips: [
      'Bis zu 10 Bilder/Videos pro Carousel',
      'Erstes Bild bestimmt das Seitenverhältnis für alle',
      'Mix aus Bildern und Videos möglich',
      'Ideal für Storytelling und Tutorials',
    ],
    lastUpdated: '2025-01-02',
  },

  // ============ FACEBOOK ============
  {
    id: 'facebook_feed_image',
    name: 'Facebook Feed Post',
    nameDe: 'Facebook Feed-Beitrag',
    platform: 'facebook',
    postType: 'feed',
    mediaType: 'image',
    aspectRatio: '1.91:1',
    aspectRatioNumeric: 1.91,
    dimensions: { width: 1200, height: 630 },
    alternativeDimensions: [
      { width: 1080, height: 1080, label: 'Quadrat (1:1)' },
      { width: 1080, height: 1350, label: 'Hochformat (4:5)' },
    ],
    maxFileSize: '30 MB',
    maxFileSizeBytes: 30 * 1024 * 1024,
    fileTypes: ['JPEG', 'PNG'],
    tips: [
      '1200×630 optimal für Link-Vorschauen',
      'Quadrat (1:1) und Hochformat (4:5) auch unterstützt',
      'Unter 100KB für beste Ladezeiten',
    ],
    lastUpdated: '2025-01-02',
  },
  {
    id: 'facebook_feed_video',
    name: 'Facebook Feed Video',
    nameDe: 'Facebook Feed-Video',
    platform: 'facebook',
    postType: 'feed',
    mediaType: 'video',
    aspectRatio: '16:9',
    aspectRatioNumeric: 1.78,
    dimensions: { width: 1920, height: 1080 },
    alternativeDimensions: [
      { width: 1080, height: 1080, label: 'Quadrat (1:1)' },
      { width: 1080, height: 1920, label: 'Vertikal (9:16)' },
    ],
    maxDuration: 14400, // 240 Minuten
    recommendedDuration: '30 Sek - 2 Min',
    maxFileSize: '10 GB',
    maxFileSizeBytes: 10 * 1024 * 1024 * 1024,
    fileTypes: ['MP4', 'MOV'],
    tips: [
      'Engagement am höchsten bei 30 Sek - 2 Min',
      '16:9 für Landscape, 1:1 oder 9:16 auch möglich',
      'Untertitel erhöhen die Reichweite deutlich',
    ],
    lastUpdated: '2025-01-02',
  },
  {
    id: 'facebook_reel',
    name: 'Facebook Reel',
    nameDe: 'Facebook Reel',
    platform: 'facebook',
    postType: 'reel',
    mediaType: 'video',
    aspectRatio: '9:16',
    aspectRatioNumeric: 0.5625,
    dimensions: { width: 1080, height: 1920 },
    alternativeDimensions: [
      { width: 1440, height: 2560, label: 'Höhere Auflösung' },
    ],
    maxDuration: 900, // 15 Minuten
    recommendedDuration: '15-60 Sekunden',
    maxFileSize: '4 GB',
    maxFileSizeBytes: 4 * 1024 * 1024 * 1024,
    fileTypes: ['MP4', 'MOV'],
    tips: [
      'Seit Juni 2025: Alle Videos werden als Reels behandelt',
      'Safe Zones beachten: 14% oben, 35% unten, 6% seitlich',
      'Kürzere Reels performen besser',
    ],
    lastUpdated: '2025-01-02',
  },
  {
    id: 'facebook_story',
    name: 'Facebook Story',
    nameDe: 'Facebook Story',
    platform: 'facebook',
    postType: 'story',
    mediaType: 'both',
    aspectRatio: '9:16',
    aspectRatioNumeric: 0.5625,
    dimensions: { width: 1080, height: 1920 },
    maxDuration: 20,
    maxFileSize: '4 GB',
    maxFileSizeBytes: 4 * 1024 * 1024 * 1024,
    fileTypes: ['JPEG', 'PNG', 'MP4', 'MOV'],
    tips: [
      'Max 20 Sekunden pro Story-Segment',
      'Videos über 10 Sek werden in Karten geteilt',
      'Bis zu 2 Minuten Gesamtlänge möglich',
    ],
    lastUpdated: '2025-01-02',
  },
  {
    id: 'facebook_carousel',
    name: 'Facebook Carousel',
    nameDe: 'Facebook Karussell',
    platform: 'facebook',
    postType: 'carousel',
    mediaType: 'both',
    aspectRatio: '1:1',
    aspectRatioNumeric: 1,
    dimensions: { width: 1080, height: 1080 },
    alternativeDimensions: [
      { width: 1200, height: 1200, label: 'Höhere Auflösung' },
    ],
    maxFileSize: '30 MB',
    maxFileSizeBytes: 30 * 1024 * 1024,
    fileTypes: ['JPEG', 'PNG', 'MP4', 'MOV'],
    tips: [
      '2-10 Bilder/Videos pro Carousel',
      'Quadratisches Format (1:1) empfohlen',
      'Konsistentes Design über alle Slides',
    ],
    lastUpdated: '2025-01-02',
  },
];

// ============ HELPER FUNCTIONS ============

/**
 * Findet die passende Format-Spezifikation basierend auf Plattform und Post-Typ
 */
export function getFormatSpec(
  platform: Platform,
  postType: PostType,
  mediaType?: MediaType
): FormatSpec | undefined {
  return FORMAT_SPECS.find(
    (spec) =>
      spec.platform === platform &&
      spec.postType === postType &&
      (mediaType === undefined || spec.mediaType === mediaType || spec.mediaType === 'both')
  );
}

/**
 * Findet alle Format-Spezifikationen für eine Plattform
 */
export function getFormatSpecsForPlatform(platform: Platform): FormatSpec[] {
  return FORMAT_SPECS.filter((spec) => spec.platform === platform);
}

/**
 * Bestimmt die Format-Kategorie basierend auf Seitenverhältnis
 */
export function getFormatCategory(aspectRatio: number): FormatCategory | undefined {
  if (aspectRatio >= 0.95 && aspectRatio <= 1.05) {
    return FORMAT_CATEGORIES.find((c) => c.id === 'square');
  }
  if (aspectRatio >= 0.75 && aspectRatio < 0.95) {
    return FORMAT_CATEGORIES.find((c) => c.id === 'portrait');
  }
  if (aspectRatio >= 0.5 && aspectRatio < 0.75) {
    return FORMAT_CATEGORIES.find((c) => c.id === 'story');
  }
  if (aspectRatio > 1.05) {
    return FORMAT_CATEGORIES.find((c) => c.id === 'landscape');
  }
  return undefined;
}

/**
 * Berechnet das Seitenverhältnis aus Breite und Höhe
 */
export function calculateAspectRatio(width: number, height: number): number {
  return width / height;
}

/**
 * Prüft, ob ein Medium mit einem Format kompatibel ist
 */
export function isMediaCompatible(
  mediaWidth: number,
  mediaHeight: number,
  mediaType: MediaType,
  mediaDuration: number | undefined,
  spec: FormatSpec
): { compatible: boolean; warnings: string[] } {
  const warnings: string[] = [];
  const mediaRatio = calculateAspectRatio(mediaWidth, mediaHeight);
  const ratioDiff = Math.abs(mediaRatio - spec.aspectRatioNumeric);

  // Seitenverhältnis prüfen
  if (ratioDiff > 0.1) {
    warnings.push(
      `Seitenverhältnis weicht ab (${mediaRatio.toFixed(2)} statt ${spec.aspectRatioNumeric.toFixed(2)}). Bild wird zugeschnitten.`
    );
  }

  // Auflösung prüfen
  if (mediaWidth < spec.dimensions.width || mediaHeight < spec.dimensions.height) {
    warnings.push(
      `Auflösung zu niedrig (${mediaWidth}×${mediaHeight}). Empfohlen: ${spec.dimensions.width}×${spec.dimensions.height}`
    );
  }

  // Video-Länge prüfen
  if (mediaType === 'video' && mediaDuration !== undefined) {
    if (spec.maxDuration && mediaDuration > spec.maxDuration) {
      warnings.push(`Video zu lang (${mediaDuration}s). Maximum: ${spec.maxDuration}s`);
    }
    if (spec.minDuration && mediaDuration < spec.minDuration) {
      warnings.push(`Video zu kurz (${mediaDuration}s). Minimum: ${spec.minDuration}s`);
    }
  }

  // Medientyp prüfen
  if (spec.mediaType !== 'both' && spec.mediaType !== mediaType) {
    warnings.push(`Dieses Format unterstützt nur ${spec.mediaType === 'image' ? 'Bilder' : 'Videos'}`);
  }

  return {
    compatible: warnings.length === 0,
    warnings,
  };
}

/**
 * Gibt die empfohlenen Formate für ein Medium zurück
 */
export function getRecommendedFormats(
  mediaWidth: number,
  mediaHeight: number,
  mediaType: MediaType,
  mediaDuration?: number
): { spec: FormatSpec; compatibility: { compatible: boolean; warnings: string[] } }[] {
  return FORMAT_SPECS.map((spec) => ({
    spec,
    compatibility: isMediaCompatible(mediaWidth, mediaHeight, mediaType, mediaDuration, spec),
  })).sort((a, b) => {
    // Kompatible zuerst, dann nach Anzahl Warnungen
    if (a.compatibility.compatible && !b.compatibility.compatible) return -1;
    if (!a.compatibility.compatible && b.compatibility.compatible) return 1;
    return a.compatibility.warnings.length - b.compatibility.warnings.length;
  });
}
